import { doc, getDoc, setDoc } from "firebase/firestore";
import type { CharacterActionKey } from "@/character/actions";
import { normalizeGridSpec } from "@/character/grid";
import { isFirebaseConfigured } from "@/auth/firebase";
import { getCloudFirestore } from "@/cloud/firebase-app";
import { SHADOWS_FIRESTORE_DOC } from "@/shadow/constants";
import {
  createEmptyShadowState,
  type ShadowDefinition,
  type ShadowPlayerState,
} from "@/shadow/types";
import { emptyAllocation, STAT_IDS } from "@/game/model/character-stats";

function privateDocRef(userId: string) {
  const db = getCloudFirestore();
  if (!db) throw new Error("Firebase ยังไม่ตั้งค่า");
  return doc(db, "users", userId, "private", SHADOWS_FIRESTORE_DOC);
}

function normalizeShadow(raw: unknown): ShadowDefinition | null {
  if (!raw || typeof raw !== "object") return null;
  const s = raw as Partial<ShadowDefinition>;
  if (typeof s.id !== "string" || typeof s.name !== "string" || typeof s.createdAt !== "number") {
    return null;
  }
  if (typeof s.spritesheetDataUrl !== "string" || !s.spritesheetDataUrl) return null;
  if (!s.grid || typeof s.grid.frameWidth !== "number") return null;
  const actions = s.actions as Record<string, string> | undefined;
  if (!actions?.idle) return null;
  const normalizedActions: Record<CharacterActionKey, string> = {
    idle: actions.idle,
    run: actions.run ?? actions.idle,
    sit: actions.sit ?? actions.idle,
    attack: actions.attack ?? actions.idle,
  };
  const allocation = emptyAllocation();
  const rawAlloc = s.statAllocation as Record<string, number> | undefined;
  if (rawAlloc) {
    for (const id of STAT_IDS) {
      const n = rawAlloc[id];
      if (typeof n === "number" && Number.isFinite(n) && n > 0) {
        allocation[id] = Math.floor(n);
      }
    }
  }
  return {
    id: s.id,
    name: s.name.trim().slice(0, 48) || "ร่างเงา",
    createdAt: s.createdAt,
    spritesheetDataUrl: s.spritesheetDataUrl,
    grid: normalizeGridSpec(s.grid),
    actions: normalizedActions,
    apCardsApplied: Math.max(0, Math.floor(s.apCardsApplied ?? 0)),
    statAllocation: allocation,
  };
}

function normalizeState(raw: unknown): ShadowPlayerState {
  const base = createEmptyShadowState();
  if (!raw || typeof raw !== "object") return base;
  const o = raw as Partial<ShadowPlayerState>;
  const shadows: ShadowDefinition[] = [];
  if (Array.isArray(o.shadows)) {
    for (const entry of o.shadows) {
      const norm = normalizeShadow(entry);
      if (norm) shadows.push(norm);
    }
  }
  const equippedId =
    typeof o.equippedId === "string" && shadows.some((s) => s.id === o.equippedId)
      ? o.equippedId
      : null;
  return { version: 1, equippedId, shadows };
}

export async function loadShadowState(userId: string): Promise<ShadowPlayerState> {
  if (!isFirebaseConfigured()) return createEmptyShadowState();
  try {
    const snap = await getDoc(privateDocRef(userId));
    if (!snap.exists()) return createEmptyShadowState();
    return normalizeState(snap.data());
  } catch {
    return createEmptyShadowState();
  }
}

export async function saveShadowState(userId: string, state: ShadowPlayerState): Promise<void> {
  if (!isFirebaseConfigured()) {
    throw new Error("ต้องตั้งค่า Firebase ก่อนบันทึกร่างเงา");
  }
  const normalized = normalizeState(state);
  await setDoc(privateDocRef(userId), normalized);
}

export function newShadowId(): string {
  return `shadow_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

export function equippedShadow(state: ShadowPlayerState): ShadowDefinition | null {
  if (!state.equippedId) return null;
  return state.shadows.find((s) => s.id === state.equippedId) ?? null;
}

export function updateShadowInState(
  state: ShadowPlayerState,
  shadow: ShadowDefinition,
): ShadowPlayerState {
  const idx = state.shadows.findIndex((s) => s.id === shadow.id);
  if (idx < 0) return state;
  const shadows = [...state.shadows];
  shadows[idx] = shadow;
  return { ...state, shadows };
}

export function removeShadowFromState(
  state: ShadowPlayerState,
  shadowId: string,
): ShadowPlayerState {
  if (!state.shadows.some((s) => s.id === shadowId)) return state;
  const shadows = state.shadows.filter((s) => s.id !== shadowId);
  const equippedId = state.equippedId === shadowId ? null : state.equippedId;
  return { ...state, shadows, equippedId };
}
